/*
  CODEX CUSTOMIZATION
  File: custom-club.js
  Scope: product template suffix "club"
  Purpose:
  - Power the club recurring/prepaid tabs.
  - Map each option card to product-form hidden inputs (id + selling_plan).
  - Keep CTA label and price panel synchronized with the selected option.
*/

(() => {
  const SELECTOR_ROOT = "[data-club-selector]";
  const SELECTOR_TAB = "[data-club-tab]";
  const SELECTOR_OPTIONS_GROUP = "[data-club-options-group]";
  const SELECTOR_OPTION = "[data-club-option]";
  const SELECTOR_OPTIONS_HEADING = "[data-club-options-heading]";
  const SELECTOR_PRICE = "[data-club-price]";
  const SELECTOR_PRICE_SUBTEXT = "[data-club-price-subtext]";
  const SELECTOR_DISCOUNT = "[data-club-discount]";
  const SELECTOR_CTA = "[data-club-cta]";
  const SELECTOR_CTA_LABEL = "[data-club-cta-label]";
  const SELECTOR_STATUS = "[data-club-status]";

  function isTrue(value) {
    return value === "true";
  }

  function findFirstOptionForTab(root, tabValue) {
    const options = Array.from(root.querySelectorAll(`${SELECTOR_OPTION}[data-club-tab-target="${tabValue}"]`));

    return options.find((option) => !option.hasAttribute("disabled")) || options[0] || null;
  }

  function setTabState(root, nextTab) {
    const recurringHeading = root.dataset.recurringHeading || "Delivery frequency";
    const prepaidHeading = root.dataset.prepaidHeading || "Gift duration";
    const heading = root.querySelector(SELECTOR_OPTIONS_HEADING);

    root.dataset.activeTab = nextTab;

    root.querySelectorAll(SELECTOR_TAB).forEach((tabButton) => {
      const isSelected = tabButton.getAttribute("data-club-tab") === nextTab;
      tabButton.classList.toggle("is-active", isSelected);
      tabButton.setAttribute("aria-selected", isSelected ? "true" : "false");
      tabButton.setAttribute("tabindex", isSelected ? "0" : "-1");
    });

    root.querySelectorAll(SELECTOR_OPTIONS_GROUP).forEach((group) => {
      const isSelected = group.getAttribute("data-club-options-group") === nextTab;
      group.hidden = !isSelected;
    });

    if (heading) {
      heading.textContent = nextTab === "prepaid" ? prepaidHeading : recurringHeading;
    }
  }

  function getOrCreateInput(form, inputName) {
    let input = form.querySelector(`input[name="${inputName}"]`);

    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = inputName;
      form.append(input);
    }

    return input;
  }

  function setFormValues(form, optionElement) {
    const variantInput = getOrCreateInput(form, "id");
    const sellingPlanInput = getOrCreateInput(form, "selling_plan");

    const variantId = optionElement.getAttribute("data-variant-id") || "";
    const sellingPlanId = optionElement.getAttribute("data-selling-plan-id") || "";

    variantInput.value = variantId;

    if (sellingPlanId !== "") {
      sellingPlanInput.value = sellingPlanId;
      sellingPlanInput.disabled = false;
    } else {
      sellingPlanInput.value = "";
      sellingPlanInput.disabled = true;
    }
  }

  function updateStatus(root, optionElement) {
    const statusElement = root.querySelector(SELECTOR_STATUS);

    if (!(statusElement instanceof HTMLElement)) {
      return;
    }

    const available = isTrue(optionElement.getAttribute("data-available"));
    const requiresSellingPlan = isTrue(optionElement.getAttribute("data-requires-selling-plan"));
    const hasSellingPlan = (optionElement.getAttribute("data-selling-plan-id") || "") !== "";
    const unavailableMessage = root.dataset.unavailableMessage || "This option is currently unavailable.";

    if (!available) {
      statusElement.textContent = unavailableMessage;
      statusElement.hidden = false;
      return;
    }

    if (requiresSellingPlan && !hasSellingPlan) {
      statusElement.textContent = "This option requires a subscription plan but none is available.";
      statusElement.hidden = false;
      return;
    }

    statusElement.textContent = "";
    statusElement.hidden = true;
  }

  function updateCta(root, optionElement) {
    const ctaButton = root.querySelector(SELECTOR_CTA);
    const ctaLabel = root.querySelector(SELECTOR_CTA_LABEL);

    if (!(ctaButton instanceof HTMLButtonElement) || !(ctaLabel instanceof HTMLElement)) {
      return;
    }

    const available = isTrue(optionElement.getAttribute("data-available"));
    const requiresSellingPlan = isTrue(optionElement.getAttribute("data-requires-selling-plan"));
    const hasSellingPlan = (optionElement.getAttribute("data-selling-plan-id") || "") !== "";

    ctaLabel.textContent = optionElement.getAttribute("data-cta-label") || "Add to cart";
    ctaButton.disabled = !available || (requiresSellingPlan && !hasSellingPlan);
  }

  function updatePricePanel(root, optionElement) {
    const priceElement = root.querySelector(SELECTOR_PRICE);
    const subtextElement = root.querySelector(SELECTOR_PRICE_SUBTEXT);
    const discountElement = root.querySelector(SELECTOR_DISCOUNT);

    if (priceElement) {
      priceElement.textContent = optionElement.getAttribute("data-price") || "";
    }

    if (subtextElement) {
      subtextElement.textContent = optionElement.getAttribute("data-price-subtext") || "";
    }

    if (discountElement) {
      const discount = optionElement.getAttribute("data-discount") || "";
      discountElement.textContent = discount;
      discountElement.hidden = discount === "";
    }
  }

  function selectOption(root, optionElement) {
    const formId = root.getAttribute("data-form-id");
    const form = formId ? document.getElementById(formId) : null;
    const selectedTab = optionElement.getAttribute("data-club-tab-target") || "recurring";

    setTabState(root, selectedTab);

    root.querySelectorAll(SELECTOR_OPTION).forEach((item) => {
      const isSelected = item === optionElement;
      item.classList.toggle("is-selected", isSelected);
      item.setAttribute("aria-pressed", isSelected ? "true" : "false");
    });

    if (form instanceof HTMLFormElement) {
      setFormValues(form, optionElement);
    }

    updatePricePanel(root, optionElement);
    updateCta(root, optionElement);
    updateStatus(root, optionElement);
  }

  function initClubSelector(root) {
    if (!(root instanceof HTMLElement) || root.dataset.clubReady === "true") {
      return;
    }

    const allOptions = Array.from(root.querySelectorAll(SELECTOR_OPTION));
    const tabs = Array.from(root.querySelectorAll(SELECTOR_TAB));

    if (allOptions.length === 0 || tabs.length === 0) {
      root.dataset.clubReady = "true";
      return;
    }

    const formId = root.getAttribute("data-form-id");
    const form = formId ? document.getElementById(formId) : null;
    const statusElement = root.querySelector(SELECTOR_STATUS);

    if (form instanceof HTMLFormElement && statusElement instanceof HTMLElement) {
      form.addEventListener("cart:error", (event) => {
        const errorText = event?.detail?.error || "Unable to add this option to cart.";
        statusElement.textContent = errorText;
        statusElement.hidden = false;
      });
    }

    tabs.forEach((tabButton) => {
      tabButton.addEventListener("click", () => {
        const nextTab = tabButton.getAttribute("data-club-tab") || "recurring";
        const selectedOption = root.querySelector(`${SELECTOR_OPTION}.is-selected`);
        const selectedOptionTab = selectedOption?.getAttribute("data-club-tab-target");

        setTabState(root, nextTab);

        if (selectedOptionTab !== nextTab) {
          const fallbackOption = findFirstOptionForTab(root, nextTab);

          if (fallbackOption instanceof HTMLElement) {
            selectOption(root, fallbackOption);
          }
        }
      });
    });

    allOptions.forEach((optionButton) => {
      optionButton.addEventListener("click", () => {
        selectOption(root, optionButton);
      });
    });

    const defaultOptionKey = root.getAttribute("data-default-option");
    const defaultTab = root.getAttribute("data-default-tab") || "recurring";

    setTabState(root, defaultTab);

    const defaultOption =
      (defaultOptionKey ? root.querySelector(`${SELECTOR_OPTION}[data-club-option="${defaultOptionKey}"]`) : null) ||
      findFirstOptionForTab(root, defaultTab) ||
      allOptions[0];

    const safeDefaultOption =
      defaultOption instanceof HTMLElement && !defaultOption.hasAttribute("disabled")
        ? defaultOption
        : findFirstOptionForTab(root, defaultTab) ||
          allOptions.find((option) => !option.hasAttribute("disabled")) ||
          defaultOption;

    if (safeDefaultOption instanceof HTMLElement) {
      selectOption(root, safeDefaultOption);
    }

    root.dataset.clubReady = "true";
  }

  function initAllClubSelectors(root = document) {
    const selectors = root instanceof Element && root.matches(SELECTOR_ROOT)
      ? [root]
      : Array.from(root.querySelectorAll(SELECTOR_ROOT));

    selectors.forEach((selectorRoot) => initClubSelector(selectorRoot));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAllClubSelectors());
  } else {
    initAllClubSelectors();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAllClubSelectors(event.target);
  });
})();
